import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { digiStoreApi, getAuthSession, getMediaUrl } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';

const PRESET_COVERS = [
  { label: 'Worksheet / Journal', url: 'https://images.unsplash.com/photo-1544717305-2782549b5136?auto=format&fit=crop&w=700&q=80' },
  { label: 'Meditation / Grounding', url: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=700&q=80' },
  { label: 'Sleep / Calm Night', url: 'https://images.unsplash.com/photo-1511295742362-92c96b124e52?auto=format&fit=crop&w=700&q=80' },
  { label: 'Emotion & DBT Skills', url: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=700&q=80' },
  { label: 'Clinical Intake Kit', url: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=700&q=80' },
  { label: 'Audio / Relaxation', url: 'https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?auto=format&fit=crop&w=700&q=80' }
];

const INITIAL_FORM_STATE = {
  title: '',
  description: '',
  category: 'Worksheets & Toolkits',
  price: 199,
  originalPrice: 499,
  currency: 'INR',
  thumbnail: '',
  format: 'Interactive PDF',
  pages: '30 Pages',
  targetAudience: 'Clients & Mental Health Practitioners',
  features: '',
  tags: '',
  fileUrl: '',
  previewUrl: '',
  status: 'approved',
  isFeatured: false,
};

function AdminDigiStorePage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Filtering & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  
  // Delete confirm modal
  const [deleteModalProduct, setDeleteModalProduct] = useState(null);

  useEffect(() => {
    fetchAdminProducts();
  }, []);

  const fetchAdminProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await digiStoreApi.adminGetProducts({ limit: 100 });
      if (res && res.success && Array.isArray(res.products)) {
        setProducts(res.products);
      } else {
        throw new Error(res.message || 'Failed to load products');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddModal = () => {
    setEditingProduct(null);
    setFormData(INITIAL_FORM_STATE);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title || '',
      description: product.description || '',
      category: product.category || 'Worksheets & Toolkits',
      price: product.price ?? 0,
      originalPrice: product.originalPrice ?? 0,
      currency: product.currency || 'INR',
      thumbnail: product.thumbnail || '',
      format: product.format || 'Interactive PDF',
      pages: product.pages || '',
      targetAudience: product.targetAudience || '',
      features: Array.isArray(product.features) ? product.features.join('\n') : (product.features || ''),
      tags: Array.isArray(product.tags) ? product.tags.join(', ') : (product.tags || ''),
      fileUrl: product.fileUrl || '',
      previewUrl: product.previewUrl || '',
      status: product.status || 'approved',
      isFeatured: Boolean(product.isFeatured),
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Please provide a title');
      return;
    }
    if (!formData.description.trim()) {
      alert('Please provide a description');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      setSuccessMsg('');

      const payload = {
        ...formData,
        price: Number(formData.price),
        originalPrice: Number(formData.originalPrice),
        features: formData.features.split('\n').map(s => s.trim()).filter(Boolean),
        tags: formData.tags.split(',').map(s => s.trim()).filter(Boolean),
      };

      if (editingProduct) {
        // Update product
        const id = editingProduct._id || editingProduct.id;
        const res = await digiStoreApi.adminUpdateProduct(id, payload);
        if (res.success) {
          setSuccessMsg(`✓ Product "${formData.title}" updated successfully!`);
          setIsModalOpen(false);
          await fetchAdminProducts();
        } else {
          throw new Error(res.message || 'Update failed');
        }
      } else {
        // Create product
        const res = await digiStoreApi.adminCreateProduct(payload);
        if (res.success) {
          setSuccessMsg(`✓ Product "${formData.title}" created successfully!`);
          setIsModalOpen(false);
          await fetchAdminProducts();
        } else {
          throw new Error(res.message || 'Creation failed');
        }
      }
    } catch (err) {
      alert(`Error: ${err.message || 'Failed to save product'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModalProduct) return;
    try {
      setActionLoading(true);
      const id = deleteModalProduct._id || deleteModalProduct.id;
      const res = await digiStoreApi.adminDeleteProduct(id);
      if (res.success) {
        setSuccessMsg(`✓ Product deleted successfully!`);
        setDeleteModalProduct(null);
        await fetchAdminProducts();
      } else {
        throw new Error(res.message || 'Delete failed');
      }
    } catch (err) {
      alert(`Error: ${err.message || 'Failed to delete product'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (product, newStatus) => {
    try {
      setActionLoading(true);
      const id = product._id || product.id;
      const res = await digiStoreApi.adminUpdateProduct(id, { status: newStatus });
      if (res.success) {
        setSuccessMsg(`Status updated to ${newStatus}`);
        await fetchAdminProducts();
      }
    } catch (err) {
      alert(`Error updating status: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleFeatured = async (product) => {
    try {
      setActionLoading(true);
      const id = product._id || product.id;
      const res = await digiStoreApi.adminUpdateProduct(id, { isFeatured: !product.isFeatured });
      if (res.success) {
        setSuccessMsg(`Featured flag updated!`);
        await fetchAdminProducts();
      }
    } catch (err) {
      alert(`Error updating feature: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Filtered products list
  const filteredProducts = products.filter(p => {
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const matchTitle = p.title?.toLowerCase().includes(q);
      const matchCat = p.category?.toLowerCase().includes(q);
      const matchDesc = p.description?.toLowerCase().includes(q);
      if (!matchTitle && !matchCat && !matchDesc) return false;
    }
    if (filterCategory !== 'all' && p.category?.toLowerCase() !== filterCategory.toLowerCase()) {
      return false;
    }
    if (filterStatus !== 'all' && p.status !== filterStatus) {
      return false;
    }
    return true;
  });

  // Calculate statistics
  const totalProducts = products.length;
  const approvedCount = products.filter(p => p.status === 'approved').length;
  const pendingCount = products.filter(p => p.status === 'pending').length;
  const totalDownloads = products.reduce((sum, p) => sum + (p.downloadCount || 0) + (p.purchaseCount || 0), 0);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
      {/* Sidebar Navigation */}
      <DashboardSidebar userRole="admin" />

      {/* Main Content Area */}
      <main style={{ marginLeft: '280px', flex: 1, padding: '36px', minWidth: 0 }}>
        
        {/* Top Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '28px'
        }}>
          <div>
            <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
              🛍️ PsychMitra DigiStore Management
            </h1>
            <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
              Manage clinical worksheets, self-help e-books, guided audio, and clinician toolkits with full CRUD control.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <Link
              to="/digistore"
              target="_blank"
              style={{
                backgroundColor: '#ffffff',
                color: '#334155',
                border: '1px solid #cbd5e1',
                padding: '10px 18px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              👁️ View Live Store
            </Link>

            <button
              onClick={handleOpenAddModal}
              style={{
                backgroundColor: '#4f46e5',
                color: '#ffffff',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 10px rgba(79, 70, 229, 0.3)'
              }}
            >
              <span style={{ fontSize: '18px' }}>+</span> Add New Product
            </button>
          </div>
        </div>

        {/* Success / Error Messages */}
        {successMsg && (
          <div style={{
            backgroundColor: '#ecfdf5',
            color: '#065f46',
            border: '1px solid #a7f3d0',
            padding: '12px 18px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{successMsg}</span>
            <button onClick={() => setSuccessMsg('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065f46', fontWeight: 'bold' }}>✕</button>
          </div>
        )}

        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            border: '1px solid #fecaca',
            padding: '12px 18px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            fontWeight: 600
          }}>
            {error}
          </div>
        )}

        {/* Stats Row */}
        <div className="row g-3" style={{ marginBottom: '28px' }}>
          <div className="col-md-3">
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Total Products</div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', marginTop: '4px' }}>{totalProducts}</div>
              <div style={{ fontSize: '12px', color: '#4f46e5', marginTop: '4px', fontWeight: 500 }}>Active DigiStore Catalog</div>
            </div>
          </div>
          <div className="col-md-3">
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Approved & Live</div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#10b981', marginTop: '4px' }}>{approvedCount}</div>
              <div style={{ fontSize: '12px', color: '#10b981', marginTop: '4px', fontWeight: 500 }}>Published to public</div>
            </div>
          </div>
          <div className="col-md-3">
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Pending Review</div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#f59e0b', marginTop: '4px' }}>{pendingCount}</div>
              <div style={{ fontSize: '12px', color: '#f59e0b', marginTop: '4px', fontWeight: 500 }}>Therapist Submissions</div>
            </div>
          </div>
          <div className="col-md-3">
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Total Downloads & Sales</div>
              <div style={{ fontSize: '26px', fontWeight: 800, color: '#8b5cf6', marginTop: '4px' }}>{totalDownloads}</div>
              <div style={{ fontSize: '12px', color: '#8b5cf6', marginTop: '4px', fontWeight: 500 }}>Clients & Practitioners</div>
            </div>
          </div>
        </div>

        {/* Filter and Search Bar */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '14px',
          padding: '16px 20px',
          border: '1px solid #e2e8f0',
          marginBottom: '24px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '14px',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', gap: '12px', flex: 1, minWidth: '280px', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search product title, category, keywords..."
              style={{
                flex: 1,
                minWidth: '200px',
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                outline: 'none'
              }}
            />

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Categories</option>
              <option value="Worksheets & Toolkits">Worksheets & Toolkits</option>
              <option value="E-Books & Manuals">E-Books & Manuals</option>
              <option value="Audio & Meditations">Audio & Meditations</option>
              <option value="Clinical Templates">Clinical Templates</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Statuses</option>
              <option value="approved">Approved & Live</option>
              <option value="pending">Pending Review</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div style={{ fontSize: '13px', color: '#64748b' }}>
            Showing <strong>{filteredProducts.length}</strong> of <strong>{products.length}</strong> items
          </div>
        </div>

        {/* Products Table Card */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <div className="spinner-border text-primary" role="status"></div>
              <p style={{ marginTop: '12px', color: '#64748b' }}>Loading DigiStore catalog...</p>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="table-responsive">
              <table className="table align-middle mb-0" style={{ fontSize: '14px' }}>
                <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <tr>
                    <th style={{ padding: '14px 20px', color: '#475569', fontWeight: 600 }}>PRODUCT</th>
                    <th style={{ padding: '14px 16px', color: '#475569', fontWeight: 600 }}>CATEGORY & FORMAT</th>
                    <th style={{ padding: '14px 16px', color: '#475569', fontWeight: 600 }}>PRICE</th>
                    <th style={{ padding: '14px 16px', color: '#475569', fontWeight: 600 }}>STATUS</th>
                    <th style={{ padding: '14px 16px', color: '#475569', fontWeight: 600 }}>DOWNLOADS</th>
                    <th style={{ padding: '14px 16px', color: '#475569', fontWeight: 600 }}>FEATURED</th>
                    <th style={{ padding: '14px 20px', color: '#475569', fontWeight: 600, textAlign: 'right' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => {
                    const id = product._id || product.id;
                    return (
                      <tr key={id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        {/* Cover + Title */}
                        <td style={{ padding: '14px 20px', maxWidth: '300px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <img
                              src={product.thumbnail ? getMediaUrl(product.thumbnail) : '/assets/images/services_new/digistore.jpeg'}
                              alt={product.title}
                              style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '8px',
                                objectFit: 'cover',
                                border: '1px solid #e2e8f0',
                                flexShrink: 0
                              }}
                            />
                            <div>
                              <div style={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.3, marginBottom: '2px' }}>
                                {product.title}
                              </div>
                              <div style={{ fontSize: '12px', color: '#64748b' }}>
                                {product.pages || 'Digital Download'}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Category & Format */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 600, color: '#334155' }}>{product.category}</div>
                          <span style={{
                            display: 'inline-block',
                            backgroundColor: '#f1f5f9',
                            color: '#64748b',
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '2px 8px',
                            borderRadius: '4px',
                            marginTop: '2px'
                          }}>
                            {product.format || 'PDF'}
                          </span>
                        </td>

                        {/* Price */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 800, color: product.price === 0 ? '#10b981' : '#0f172a', fontSize: '15px' }}>
                            {product.price === 0 ? 'FREE' : `₹${product.price}`}
                          </div>
                          {product.originalPrice > product.price && (
                            <div style={{ fontSize: '12px', color: '#94a3b8', textDecoration: 'line-through' }}>
                              ₹{product.originalPrice}
                            </div>
                          )}
                        </td>

                        {/* Status */}
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '50px',
                            fontSize: '12px',
                            fontWeight: 700,
                            backgroundColor: product.status === 'approved' ? '#ecfdf5' : product.status === 'pending' ? '#fffbeb' : '#fef2f2',
                            color: product.status === 'approved' ? '#065f46' : product.status === 'pending' ? '#b45309' : '#991b1b',
                            border: `1px solid ${product.status === 'approved' ? '#a7f3d0' : product.status === 'pending' ? '#fde68a' : '#fecaca'}`
                          }}>
                            {product.status === 'approved' ? '✓ Approved' : product.status === 'pending' ? '⏳ Pending' : '✕ Rejected'}
                          </span>
                        </td>

                        {/* Downloads */}
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 700, color: '#334155' }}>
                            {(product.downloadCount || 0) + (product.purchaseCount || 0)}
                          </div>
                          <div style={{ fontSize: '12px', color: '#f59e0b' }}>
                            ★ {product.rating || 4.8}
                          </div>
                        </td>

                        {/* Featured Toggle */}
                        <td style={{ padding: '14px 16px' }}>
                          <button
                            type="button"
                            onClick={() => handleToggleFeatured(product)}
                            style={{
                              backgroundColor: product.isFeatured ? '#fef3c7' : '#f1f5f9',
                              color: product.isFeatured ? '#b45309' : '#94a3b8',
                              border: 'none',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer'
                            }}
                          >
                            {product.isFeatured ? '⭐ Featured' : '☆ Standard'}
                          </button>
                        </td>

                        {/* Action Buttons */}
                        <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '8px' }}>
                            <button
                              type="button"
                              onClick={() => handleOpenEditModal(product)}
                              title="Edit Product"
                              style={{
                                backgroundColor: '#eef2ff',
                                color: '#4f46e5',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              ✏️ Edit
                            </button>

                            {product.status === 'pending' && (
                              <button
                                type="button"
                                onClick={() => handleToggleStatus(product, 'approved')}
                                title="Approve"
                                style={{
                                  backgroundColor: '#ecfdf5',
                                  color: '#059669',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '6px 10px',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                ✓ Approve
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => setDeleteModalProduct(product)}
                              title="Delete Product"
                              style={{
                                backgroundColor: '#fef2f2',
                                color: '#ef4444',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '6px 10px',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <p style={{ color: '#64748b', fontSize: '16px', margin: '0 0 16px 0' }}>No products match your filters.</p>
              <button
                type="button"
                onClick={handleOpenAddModal}
                style={{
                  backgroundColor: '#4f46e5',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                + Create First Digital Product
              </button>
            </div>
          )}
        </div>

      </main>

      {/* Add / Edit Product Modal */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(4px)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            maxWidth: '750px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '30px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                {editingProduct ? '✏️ Edit DigiStore Product' : '✨ Add New DigiStore Product'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                {/* Title */}
                <div className="col-12">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Product Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g. CBT Thought Record & Restructuring Workbook"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                {/* Category & Format */}
                <div className="col-md-6">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff' }}
                  >
                    <option value="Worksheets & Toolkits">Worksheets & Toolkits</option>
                    <option value="E-Books & Manuals">E-Books & Manuals</option>
                    <option value="Audio & Meditations">Audio & Meditations</option>
                    <option value="Clinical Templates">Clinical Templates</option>
                    <option value="Video Courses">Video Courses</option>
                    <option value="Wellness Packages">Wellness Packages</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Format
                  </label>
                  <input
                    type="text"
                    value={formData.format}
                    onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                    placeholder="e.g. Interactive PDF, MP3 Audio, Word (.docx)"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                {/* Pricing: Price and Original Price */}
                <div className="col-md-6">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Sale Price (₹) * (Set 0 for Free)
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                <div className="col-md-6">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Original Price / MRP (₹) (For discount badge)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.originalPrice}
                    onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                {/* Pages & Target Audience */}
                <div className="col-md-6">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Pages / Duration
                  </label>
                  <input
                    type="text"
                    value={formData.pages}
                    onChange={(e) => setFormData({ ...formData, pages: e.target.value })}
                    placeholder="e.g. 42 Pages or 45 Mins Audio"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                <div className="col-md-6">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Target Audience
                  </label>
                  <input
                    type="text"
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                    placeholder="e.g. Clients, Psychology Students & Therapists"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                {/* Thumbnail Cover URL */}
                <div className="col-12">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Thumbnail Image URL
                  </label>
                  <input
                    type="text"
                    value={formData.thumbnail}
                    onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                    placeholder="Enter image URL or select a preset below"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                  {/* Preset cover shortcuts */}
                  <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Quick Presets:</span>
                    {PRESET_COVERS.map((preset, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setFormData({ ...formData, thumbnail: preset.url })}
                        style={{
                          background: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          padding: '2px 8px',
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div className="col-12">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Clinical Description *
                  </label>
                  <textarea
                    rows="3"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Comprehensive description of therapeutic benefits, evidence base, and methodology..."
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                {/* Features (One per line) */}
                <div className="col-12">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Key Features / What's Included (One item per line)
                  </label>
                  <textarea
                    rows="3"
                    value={formData.features}
                    onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                    placeholder="Fillable digital PDF with auto-save support&#10;Clinician-approved 7-column thought records&#10;Printable 300 DPI high resolution"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                {/* Tags */}
                <div className="col-md-6">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Tags (Comma separated)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="CBT, Anxiety, Sleep, Depression"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                {/* Status & Featured */}
                <div className="col-md-6">
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '4px', display: 'block' }}>
                    Publication Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff' }}
                  >
                    <option value="approved">Approved & Live</option>
                    <option value="pending">Pending Review</option>
                    <option value="rejected">Rejected / Draft</option>
                  </select>
                </div>

                <div className="col-12" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                  <input
                    type="checkbox"
                    id="isFeaturedCheck"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="isFeaturedCheck" style={{ fontSize: '14px', fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
                    Feature on DigiStore Home (Highlights with ⭐ Top Pick Badge)
                  </label>
                </div>

              </div>

              {/* Submit Buttons */}
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  style={{
                    backgroundColor: '#4f46e5',
                    color: '#ffffff',
                    border: 'none',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: actionLoading ? 'not-allowed' : 'pointer',
                    opacity: actionLoading ? 0.7 : 1
                  }}
                >
                  {actionLoading ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.7)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            maxWidth: '450px',
            width: '100%',
            padding: '28px',
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '14px' }}>⚠️</span>
            <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
              Confirm Deletion
            </h3>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
              Are you sure you want to delete <strong>"{deleteModalProduct.title}"</strong> from PsychMitra DigiStore? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => setDeleteModalProduct(null)}
                style={{
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={actionLoading}
                style={{
                  backgroundColor: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {actionLoading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminDigiStorePage;
